import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { key: 0 }

import { ref, watch, onBeforeUnmount } from 'vue';
import type { FormField } from '@/types/form';

// 🔹 NUEVO: mismos helpers que usas en CreateForm
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import debounce from 'lodash.debounce';


export default /*@__PURE__*/_defineComponent({
  __name: 'EvidenceForm',
  props: {
    fields: {},
    formTitle: {},
    initialValues: {},
    onSubmit: { type: Function },
    onCancel: { type: Function },
    multiple: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const form = ref<Record<string, any>>({});
const formRef = ref();
const formValid = ref(true);

// 🔹 NUEVO: estados para loaders por campo y debounced fetchers
const fieldLoading = ref<Record<string, boolean>>({});
const debouncedFetchers: Record<string, (val: string) => void> = {};

// 🔹 NUEVO: arma filtros para fetchOptions
const getFetchParams = (field: FormField, currentForm: any, val: string = '') => {
  let params: Record<string, any> = {};
  if (field.searchValue && val !== '') {
    params[`filter[${field.searchValue}]`] = val;
  }
  if (typeof field.fetchParams === 'function') {
    params = { ...params, ...field.fetchParams(currentForm) };
  }
  return params;
};

// Inicializa el form (soporta initialValues)
const resetForm = () => {
  fieldLoading.value = {};
  props.fields.forEach((f) => {
    if (f.isJsonArray) {
      form.value[f.value] = props.initialValues?.[f.value] || [{}];
    } else {
      form.value[f.value] = props.initialValues?.[f.value] ?? (f.multiple ? [] : '');
    }
  });
};
resetForm();

// Limpia el formulario si cambian los campos o initialValues
watch(
  () => [props.fields, props.initialValues],
  resetForm,
  { immediate: false }
);

// Limpia el form al desmontar
onBeforeUnmount(() => {
  Object.keys(form.value).forEach((k) => (form.value[k] = ''));
});

const getFieldProps = (field: FormField) => {
  const p: Record<string, any> = {
    label: field.label,
    placeholder: field.placeholder ?? '',
    clearable: true,
    density: 'compact',
    type: field.type || 'text',
    prefix: field.prefix,
    suffix: field.suffix,
    'prepend-inner-icon': field.icon,
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    multiple: field.multiple || false,
    range: field.range,
    module: field.module,
    loading: !!fieldLoading.value[field.value],
    // títulos/valores por defecto
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    'hide-no-data': true,
    ...field.props, // Permite pasar props personalizados por campo
  };

  // Asegura que items sea reactivo
  p.items = field.options || [];

  // 🔹 NUEVO: Soporte de fetchOptions (Autocomplete/Select remoto)
  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    const fetchFn = remoteOptionsRegistry[field.fetchOptions];

    // Crea el debounced una sola vez
    if (!debouncedFetchers[field.value]) {
      debouncedFetchers[field.value] = debounce(async (val: string) => {
        fieldLoading.value[field.value] = true;
        try {
          const params = getFetchParams(field, form.value, val);
          const results = await fetchFn(params);
          field.options = Array.isArray(results) ? [...results] : [];
        } finally {
          fieldLoading.value[field.value] = false;
        }
      }, 500);
    }

    // 1) Fetch al enfocar (abre dropdown sin search para pre-cargar)
    p.onFocus = (open: boolean) => {
      if (open && (!field.options || field.options.length === 0)) {
        fieldLoading.value[field.value] = true;
        fetchFn(getFetchParams(field, form.value, ''))
          .then((results: any[]) => {
            field.options = Array.isArray(results) ? [...results] : [];
          })
          .finally(() => (fieldLoading.value[field.value] = false));
      }
    };

    // 2) Búsqueda en vivo (Vuetify 3: v-model:search -> update:search)
    p['onUpdate:search'] = (val: string) => {
      debouncedFetchers[field.value](val || '');
    };
  }

  return p;
};

const submitForm = () => {
  if (!formRef.value?.validate()) return;
  props.onSubmit({ ...form.value });
  resetForm();
};

const cancelForm = () => {
  resetForm();
  props.onCancel?.();
};

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submitForm, ["prevent"]),
    ref_key: "formRef",
    ref: formRef,
    modelValue: formValid.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formValid).value = $event))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "px-5 py-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center mb-3" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(_ctx.formTitle), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              (!_ctx.fields?.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createVNode(_component_v_alert, {
                      type: "warning",
                      border: "start",
                      prominent: ""
                    }, {
                      default: _withCtx(() => _cache[1] || (_cache[1] = [
                        _createTextVNode(" No hay campos definidos para este formulario. ")
                      ])),
                      _: 1,
                      __: [1]
                    })
                  ]))
                : (_openBlock(), _createBlock(_component_v_row, { key: 1 }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.fields, (field) => {
                        return (_openBlock(), _createBlock(_component_v_col, {
                          key: field.value,
                          cols: 12,
                          md: field.col || 12,
                          sm: field.mdcol || 12
                        }, {
                          default: _withCtx(() => [
                            (!field.isJsonArray)
                              ? (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), _mergeProps({
                                  key: 0,
                                  modelValue: form.value[field.value],
                                  "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event)
                                }, { ref_for: true }, getFieldProps(field), {
                                  rules: field.required ? [v => !!v || 'Campo obligatorio'] : [],
                                  variant: "solo-filled"
                                }), null, 16, ["modelValue", "onUpdate:modelValue", "rules"]))
                              : (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), {
                                  key: 1,
                                  modelValue: form.value[field.value],
                                  "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event),
                                  fields: field.children,
                                  label: field.label,
                                  summary: field.summary,
                                  multiple: _ctx.multiple,
                                  variant: "solo-filled"
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "fields", "label", "summary", "multiple"]))
                          ]),
                          _: 2
                        }, 1032, ["md", "sm"]))
                      }), 128))
                    ]),
                    _: 1
                  }))
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                variant: "text",
                onClick: cancelForm
              }, {
                default: _withCtx(() => _cache[2] || (_cache[2] = [
                  _createTextVNode("Cancelar")
                ])),
                _: 1,
                __: [2]
              }),
              _createVNode(_component_v_btn, {
                type: "submit",
                color: "primary",
                disabled: !formValid.value
              }, {
                default: _withCtx(() => _cache[3] || (_cache[3] = [
                  _createTextVNode("Confirmar")
                ])),
                _: 1,
                __: [3]
              }, 8, ["disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})